import React, { useState, useEffect, useContext } from 'react'
import { LangContext } from '../../context/lang-context'
import { NotificationContext } from '../../context/notification-context'
import useFetch from '../../hooks/fetchMSSQL-hook'
import useForm from '../../hooks/form-hook'

import TextInput from '../FormElements/TextInput'
import DatalistInput from '../FormElements/DatalistInput'

import { Prompt } from 'react-router-dom/cjs/react-router-dom.min'
import TagsFilter from '../Modals/TagsFilter'
import { NotificationManager } from 'react-notifications'
import CoffeeContainers from '../CoffeeContainers/CoffeeContainers'
import MachineIngredientsModal from '../CoffeeContainers/MachineIngredientsModal'

const normalizeValue = value => String(value === null || value === undefined ? '' : value).trim().toLowerCase()


const isEnabledFlag = value => {
  const normalizedValue = normalizeValue(value)

  return value === true || value === 1 || normalizedValue === '1' || normalizedValue === 'true'
}

const getMaintenanceLabel = maintenance => {
  const name = maintenance?.Name || ''

  if (isEnabledFlag(maintenance?.IsCoffee ?? maintenance?.isCoffee)) return `${name} (KAWA)`
  if (isEnabledFlag(maintenance?.IsSnack ?? maintenance?.isSnack)) return `${name} (SNACK)`

  return name
}

export default ({ machineData: initialMachineData, updateMachine, machineId, data }) => {
  const { TRL_Pack } = useContext(LangContext)
  const { ErrorNotification } = useContext(NotificationContext)
  const { fetchMssqlApi } = useFetch()

  const [machineData, setMachineData] = useState({
    machineName: initialMachineData.MachineName || '',
    location: initialMachineData.LocationName || '',
    machineType: initialMachineData.MachineTypeName || '',
    maintenance: initialMachineData.MaintenanceName || '',
    machineTags: initialMachineData.MachineTags,
    isActive: initialMachineData.IsActive,
    serialNo: initialMachineData.SerialNo || '',
    cashRegister: initialMachineData.CashRegister || '',
    terminal: initialMachineData.TerminalName || initialMachineData.TerminalSerialNo || '',
    advertise: initialMachineData.AdvertiseName || '',
  })

  const { form, openForm, closeForm } = useForm()

  const [isUnsavedData, setIsUnsavedData] = useState(false)
  const [locationsData, setLocationsData] = useState([])
  const [maintenancesData, setMaintenancesData] = useState([])
  const [machineTypesData, setMachineTypesData] = useState([])
  const [tags, setTags] = useState([])
  const [cashRegisters, setCashRegisters] = useState([])
  const [showIngredientsModal, setShowIngredientsModal] = useState(false)
  const [ingredientsRefreshKey, setIngredientsRefreshKey] = useState(0)

  useEffect(() => {
    const hasChanges =
      machineData.machineName !== (initialMachineData.MachineName || '') ||
      machineData.location !== (initialMachineData.LocationName || '') ||
      machineData.machineType !== (initialMachineData.MachineTypeName || '') ||
      machineData.maintenance !== (initialMachineData.MaintenanceName || '') ||
      machineData.serialNo !== (initialMachineData.SerialNo || '') ||
      machineData.cashRegister !== (initialMachineData.CashRegister || '')

    setIsUnsavedData(hasChanges)
  }, [machineData, initialMachineData])

  const handleChange = evt => {
    const { name, value } = evt.target

    if (name === 'maintenance') {
      const selectedMaintenance = maintenancesData.find(
        maintenance => normalizeValue(getMaintenanceLabel(maintenance)) === normalizeValue(value)
      )

      setMachineData(prev => ({
        ...prev,
        maintenance: selectedMaintenance?.Name ?? value
      }))
      return
    }

    setMachineData(prev => ({ ...prev, [name]: value }))
  }

  const focusHandler = evt => {
    if (typeof evt.target.select === 'function') {
      evt.target.select()
    }
  }

  const getLocations = () => {
    fetchMssqlApi(
      'locations',
      {},
      locations => {
        setLocationsData(Array.isArray(locations) ? locations : [])
      }
    )
  }

  const getMachineTypes = () => {
    fetchMssqlApi(
      'machine-types',
      {},
      machineTypes => {
        setMachineTypesData(Array.isArray(machineTypes) ? machineTypes : [])
      }
    )
  }

  const getMaintenances = () => {
    fetchMssqlApi(
      'maintenance-types',
      { method: 'POST', hideNotification: true, data: { isActive: 1 } },
      maintenances => {
        const maintenanceTypes = Array.isArray(maintenances) ? maintenances : []
        const selectedMaintenance = maintenanceTypes.find(maintenance =>
          Number(maintenance?.MaintenanceTypeId) === Number(initialMachineData.MaintenanceTypeId) ||
          normalizeValue(maintenance?.Name) === normalizeValue(initialMachineData.MaintenanceName)
        )

        setMaintenancesData(maintenanceTypes)

        if (selectedMaintenance) {
          updateMachine(prev => ({
            ...prev,
            IsCoffee: selectedMaintenance?.IsCoffee ?? selectedMaintenance?.isCoffee ?? prev.IsCoffee,
            IsSnack: selectedMaintenance?.IsSnack ?? selectedMaintenance?.isSnack ?? prev.IsSnack
          }))
        }
      }
    )
  }

const getTags = () => {
  fetchMssqlApi(
    `tags/${initialMachineData.MachineId}`,
    {},
    response => {
      const responseTags = Array.isArray(response)
        ? response
        : Array.isArray(response?.machine)
          ? response.machine
          : []

      const mappedTags = responseTags.reduce((groups, tag) => {
        const fullName = String(tag.Name ?? tag.name ?? '').trim()
        const separator = ' - '
        const separatorIndex = fullName.indexOf(separator)

        const label = separatorIndex >= 0
          ? fullName.substring(0, separatorIndex).trim()
          : ''

        const name = separatorIndex >= 0
          ? fullName.substring(separatorIndex + separator.length).trim()
          : fullName

        const machineTagId =
          tag.MachineTagId ??
          tag.machineTagId ??
          null

        let group = groups.find(group => group.label === label)

        if (!group) {
          group = {
            label,
            options: []
          }

          groups.push(group)
        }

        group.options.push({
          ...tag,
          tagId: tag.TagId ?? tag.tagId,
          name,
          machineTagId,
          isActive: machineTagId !== null
        })

        return groups
      }, [])

      setTags(mappedTags)
    }
  )
}

  const getCashRegisters = () => {
    return fetchMssqlApi(
      'cashRegisters/null',
      {},
      response => {
        setCashRegisters(Array.isArray(response) ? response : [])
      }
    )
  }

  useEffect(() => {
    getLocations()
    getMachineTypes()
    getTags()
    getCashRegisters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getMaintenances()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showInvalidInput = message => {
    const errorMessage = message || TRL_Pack?.errors?.invalidInput || 'Nieprawidłowe dane formularza'

    if (typeof ErrorNotification === 'function') {
      ErrorNotification(errorMessage)
    }
    else {
      NotificationManager.error(errorMessage)
    }
  }

  const saveTagChanges = () => {
    const postData = []
    const deleteData = []

    if (!Array.isArray(tags)) return

    tags.forEach(label => {
      if (!Array.isArray(label?.options)) return

      label.options.forEach(option => {
        if (option.isActive && option.machineTagId === null) {
          postData.push(option.tagId)
        }
        else if (!option.isActive && option.machineTagId !== null) {
          deleteData.push(option.machineTagId)
        }
      })
    })

    postData.forEach(tagId => {
      const payload = {
        MachineId: parseInt(machineId),
        TagId: parseInt(tagId)
      }

      fetchMssqlApi(
        'machine-tag',
        { method: 'POST', data: payload },
        () => {
          getTags()
        }
      )
    })

    deleteData.forEach(machineTagId => {

      fetchMssqlApi(
        `machine-tag/${machineTagId}`,
        { method: 'DELETE' },
        () => {
          getTags()
        }
      )
    })
  }

  const saveMachine = () => {
    if (!machineData.isActive) {
      NotificationManager.warning('Nie można zapisać nieaktywnego automatu')
      return
    }

    const machineName = String(machineData.machineName || '').trim()
    const serialNo = String(machineData.serialNo || '').trim()
    const locationName = String(machineData.location || '').trim()
    const machineTypeName = String(machineData.machineType || '').trim()
    const maintenanceName = String(machineData.maintenance || '').trim()
    const cashRegisterNo = String(machineData.cashRegister || '').trim()

    if (!machineName || !serialNo || !locationName || !machineTypeName || !maintenanceName) {
      showInvalidInput('Uzupełnij wszystkie wymagane pola')
      return
    }

    const selectedLocation = locationsData.find(
      location => normalizeValue(location?.Name) === normalizeValue(locationName)
    )
    const selectedMachineType = machineTypesData.find(
      machineType => normalizeValue(machineType?.Name) === normalizeValue(machineTypeName)
    )
    const selectedMaintenance = maintenancesData.find(
      maintenance => normalizeValue(maintenance?.Name) === normalizeValue(maintenanceName)
    )
    const selectedCashRegister = cashRegisterNo
      ? cashRegisters.find(
          cashRegister => normalizeValue(cashRegister?.NrUnik) === normalizeValue(cashRegisterNo)
        )
      : null

    const locationId = selectedLocation?.LocationId ?? (
      normalizeValue(locationName) === normalizeValue(initialMachineData.LocationName)
        ? initialMachineData.LocationId
        : null
    )
    const machineTypeId = selectedMachineType?.MachineTypeId ?? (
      normalizeValue(machineTypeName) === normalizeValue(initialMachineData.MachineTypeName)
        ? initialMachineData.MachineTypeId
        : null
    )
    const maintenanceTypeId = selectedMaintenance?.MaintenanceTypeId ?? (
      normalizeValue(maintenanceName) === normalizeValue(initialMachineData.MaintenanceName)
        ? initialMachineData.MaintenanceTypeId
        : null
    )
    const cashRegisterId = cashRegisterNo
      ? selectedCashRegister?.CashRegisterId ?? (
          normalizeValue(cashRegisterNo) === normalizeValue(initialMachineData.CashRegister)
            ? initialMachineData.CashRegisterId
            : null
        )
      : null

    if (locationId === null || locationId === undefined) {
      showInvalidInput(`Nie znaleziono lokalizacji: ${locationName}`)
      return
    }

    if (machineTypeId === null || machineTypeId === undefined) {
      showInvalidInput(`Nie znaleziono typu automatu: ${machineTypeName}`)
      return
    }

    if (maintenanceTypeId === null || maintenanceTypeId === undefined) {
      showInvalidInput(`Nie znaleziono serwisu: ${maintenanceName}`)
      return
    }

    if (cashRegisterNo && (cashRegisterId === null || cashRegisterId === undefined)) {
      showInvalidInput(`Nie znaleziono kasy fiskalnej: ${cashRegisterNo}`)
      return
    }

    const updatedMachine = {
      machineId: initialMachineData.MachineId,
      serialNo,
      name: machineName,
      locationId: Number(locationId),
      machineTypeId: Number(machineTypeId),
      maintenanceTypeId: Number(maintenanceTypeId),
      cashRegisterId: cashRegisterId === null || cashRegisterId === undefined
        ? null
        : Number(cashRegisterId),
      isActive: machineData.isActive
    }

    try {
      fetchMssqlApi(
        'machine',
        { method: 'PUT', data: updatedMachine },
        () => {
          updateMachine(prev => ({
            ...prev,
            MachineName: machineName,
            LocationName: locationName,
            LocationId: Number(locationId),
            MachineTypeName: machineTypeName,
            MachineTypeId: Number(machineTypeId),
            MaintenanceName: maintenanceName,
            MaintenanceTypeId: Number(maintenanceTypeId),
            IsCoffee: selectedMaintenance?.IsCoffee ?? selectedMaintenance?.isCoffee ?? initialMachineData.IsCoffee,
            IsSnack: selectedMaintenance?.IsSnack ?? selectedMaintenance?.isSnack ?? initialMachineData.IsSnack,
            SerialNo: serialNo,
            CashRegister: cashRegisterNo,
            CashRegisterId: cashRegisterId === null || cashRegisterId === undefined
              ? null
              : Number(cashRegisterId),
            IsActive: machineData.isActive
          }))

          setMachineData(prev => ({
            ...prev,
            machineName,
            location: locationName,
            machineType: machineTypeName,
            maintenance: maintenanceName,
            serialNo,
            cashRegister: cashRegisterNo
          }))

          setIsUnsavedData(false)
          saveTagChanges()
          NotificationManager.success('Zapisano zmiany automatu')
        },
        () => {
          NotificationManager.error('Błąd podczas zmiany automatu')
        }
      )
    }
    catch {
      NotificationManager.error('Błąd podczas zmiany automatu')
    }
  }

  const handleSubmit = evt => {
    if (evt && typeof evt.preventDefault === 'function') {
      evt.preventDefault()
    }

    saveMachine()
  }

  const selectedMaintenanceMode = maintenancesData.find(
    maintenance => normalizeValue(maintenance?.Name) === normalizeValue(machineData.maintenance)
  )

  const maintenanceIsSnack = isEnabledFlag(
    selectedMaintenanceMode?.IsSnack ??
    selectedMaintenanceMode?.isSnack ??
    initialMachineData.IsSnack ??
    initialMachineData.MaintenanceIsSnack ??
    initialMachineData.MaintenanceTypeIsSnack ??
    data?.IsSnack ??
    data?.MaintenanceIsSnack
  )

  const maintenanceIsCoffee = isEnabledFlag(
    selectedMaintenanceMode?.IsCoffee ??
    selectedMaintenanceMode?.isCoffee ??
    initialMachineData.IsCoffee ??
    initialMachineData.MaintenanceIsCoffee ??
    initialMachineData.MaintenanceTypeIsCoffee ??
    data?.IsCoffee ??
    data?.MaintenanceIsCoffee
  )

  const isCoffee = !maintenanceIsSnack && maintenanceIsCoffee

  const discardChanges = () => {
    setMachineData({
      machineName: initialMachineData.MachineName || '',
      location: initialMachineData.LocationName || '',
      machineType: initialMachineData.MachineTypeName || '',
      maintenance: initialMachineData.MaintenanceName || '',
      machineTags: initialMachineData.MachineTags,
      isActive: initialMachineData.IsActive,
      serialNo: initialMachineData.SerialNo || '',
      cashRegister: initialMachineData.CashRegister || '',
      terminal: initialMachineData.TerminalName || initialMachineData.TerminalSerialNo || '',
      advertise: initialMachineData.AdvertiseName || ''
    })
  }

  const changeMachineStatus = () => {
    const updatedStatus = !initialMachineData.IsActive
    const statusPayload = {
      machineId: initialMachineData.MachineId,
      name: initialMachineData.MachineName,
      locationId: initialMachineData.LocationId,
      machineTypeId: initialMachineData.MachineTypeId,
      maintenanceTypeId: initialMachineData.MaintenanceTypeId,
      cashRegisterId: initialMachineData.CashRegisterId,
      serialNo: initialMachineData.SerialNo,
      isActive: updatedStatus
    }

    fetchMssqlApi(
      'machine',
      { method: 'PUT', data: statusPayload },
      () => {
        updateMachine(prev => ({
          ...prev,
          MachineName: initialMachineData.MachineName,
          LocationName: initialMachineData.LocationName,
          MachineTypeName: initialMachineData.MachineTypeName,
          MaintenanceName: initialMachineData.MaintenanceName,
          SerialNo: initialMachineData.SerialNo,
          IsActive: updatedStatus
        }))

        setMachineData(prev => ({
          ...prev,
          isActive: updatedStatus
        }))

        setIsUnsavedData(false)
      },
      () => {
        NotificationManager.error('Błąd podczas zmiany automatu')
      }
    )
  }
  return (
    <div className="row mb-4">
      <div className="col-12 col-md-6 mb-4 mb-md-0">
        <div className="card h-100">
          <h5 className="card-header">{TRL_Pack.machine.settings.header}</h5>
          <div className="card-body d-flex flex-column justify-content-center">
            <Prompt
              when={isUnsavedData}
              message={TRL_Pack.machineTypes.confirmUnsaved}
            />
            <form onSubmit={handleSubmit} id="machine-form" autoComplete="off" noValidate>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  Nr seryjny
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap">
                  <TextInput
                    // className="mx-auto mx-lg-0"
                    style={{ maxWidth: 275, borderRadius: ".2rem 0 0 .2rem" }}
                    name="serialNo"
                    id="serialNo"
                    value={machineData.serialNo}
                    handleChange={handleChange}
                    minLength={1}
                    onFocus={focusHandler}
                    disabled={!machineData.isActive}
                    required
                  />
                  <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.serialNo); NotificationManager.success(`"${machineData.serialNo}" skopiowano do schowka`)}}>
                    <i className="fas fa-light fa-clone"/> 
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  {TRL_Pack.machines.properties.machineName}
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap align-items-center">
                  <TextInput
                    // className="mx-auto mx-lg-0"
                    style={{ maxWidth: 275, borderRadius: ".2rem 0 0 .2rem" }}
                    name="machineName"
                    value={machineData.machineName}
                    handleChange={handleChange}
                    minLength={2}
                    maxLength={50}
                    onFocus={focusHandler}
                    disabled={!machineData.isActive}
                    required
                  />

                <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.machineName); NotificationManager.success(`"${machineData.machineName}" skopiowano do schowka`)}}>
                  <i className="fas fa-light fa-clone"/> 
                </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  {TRL_Pack.machines.properties.location}
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap">
                  <DatalistInput
                    style={{ maxWidth: 275, borderRadius: ".2rem 0 0 .2rem"}}
                    // className="mx-auto mx-lg-0 mr-0"
                    name="location"
                    value={machineData.location}
                    handleChange={handleChange}
                    onFocus={focusHandler}
                    list={locationsData.map(location => location.Name)}
                    disabled={!machineData.isActive}
                    newList
                  />
                  <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.location); NotificationManager.success(`"${machineData.location}" skopiowano do schowka`)}}>
                    <i className="fas fa-light fa-clone" /> 
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  {TRL_Pack.machines.properties.machineType}
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap">
                  <DatalistInput
                    style={{ maxWidth: 275 }}
                    // className="mx-auto mx-lg-0"
                    name="machineType"
                    value={machineData.machineType}
                    handleChange={handleChange}
                    onFocus={focusHandler}
                    list={machineTypesData.map(machineType => machineType.Name)}
                    disabled={!machineData.isActive}
                    newList
                  />
                  <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.machineType); NotificationManager.success(`"${machineData.machineType}" skopiowano do schowka`)}}>
                    <i className="fas fa-light fa-clone" /> 
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  {TRL_Pack.machines.properties.maintenance}
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap ">
                  <DatalistInput
                    style={{ maxWidth: 275 }}
                    // className="mx-auto mx-lg-0"
                    name="maintenance"
                    value={selectedMaintenanceMode ? getMaintenanceLabel(selectedMaintenanceMode) : machineData.maintenance}
                    handleChange={handleChange}
                    onFocus={focusHandler}
                    list={maintenancesData.map(getMaintenanceLabel)}
                    disabled={!machineData.isActive}
                    newList
                  />
                  <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.maintenance); NotificationManager.success(`"${machineData.maintenance}" skopiowano do schowka`)}}>
                    <i className="fas fa-light fa-clone" /> 
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  Kasa fiskalna
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap">
                  <DatalistInput
                    style={{ maxWidth: 275 }}
                    // className="mx-auto mx-lg-0"
                    name="cashRegister"
                    required={false}
                    minLength={0}
                    value={machineData.cashRegister}
                    handleChange={handleChange}
                    onFocus={focusHandler}
                    list={cashRegisters.map(cr => cr.NrUnik)}
                    disabled={!machineData.isActive}
                    newList
                  />
                  <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.cashRegister); NotificationManager.success(`"${machineData.cashRegister}" skopiowano do schowka`)}}>
                    <i className="fas fa-light fa-clone" /> 
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  Terminal
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap">
                  <TextInput
                    style={{ maxWidth: 275, borderRadius: ".2rem 0 0 .2rem" }}
                    name="terminal"
                    value={machineData.terminal}
                    handleChange={handleChange}
                    minLength={0}
                    disabled
                  />
                  <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.terminal || ''); NotificationManager.success(`"${machineData.terminal || ''}" skopiowano do schowka`)}}>
                    <i className="fas fa-light fa-clone" />
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">
                  Zestaw reklam
                </div>
                <div className="col-lg-7 my-auto d-flex nowrap">
                  <TextInput
                    style={{ maxWidth: 275, borderRadius: ".2rem 0 0 .2rem" }}
                    name="advertise"
                    value={machineData.advertise}
                    handleChange={handleChange}
                    minLength={0}
                    disabled
                  />
                  <div className='d-flex align-items-center' style={{border: "1px solid #ced4da", borderRadius: "0 .2rem .2rem 0", height: "calc(1.5em + .5rem - 1.5px )", paddingLeft: "2px", paddingRight: "2px"}} onClick={() => {navigator.clipboard.writeText(machineData.advertise || ''); NotificationManager.success(`"${machineData.advertise || ''}" skopiowano do schowka`)}}>
                    <i className="fas fa-light fa-clone" />
                  </div>
                </div>
              </div>
              <div className="row mb-n1">
                <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">{TRL_Pack.navbar.tags}</div>
                <div className="col-lg-8 my-auto">
                  {tags?.map(tag =>
                    tag?.options?.filter(opt => opt)
                      .map(opt => {
                        if (opt.isActive === true) {
                          return (
                            <button
                              key={opt.tagId}
                              type="button"
                              className="btn btn-info badge badge-pill px-2 py-1 mx-2 mb-1"
                            >
                              {tag.label ? `${tag.label} - ${opt.name}` : opt.name}
                            </button>

                          )
                        }
                        else if (opt.isActive === true && opt.machineTagId !== null) {
                          return (
                            <button
                              key={opt.tagId}
                              type="button"
                              className="btn btn-info badge badge-pill px-2 py-1 mx-2 mb-1"
                            >
                              {tag.label ? `${tag.label} - ${opt.name}` : opt.name}
                            </button>
                          )
                        }
                      }
                      )
                  )}
                  {machineData.isActive && (
                    <button
                      type="button"
                      className="btn btn-info badge badge-pill px-2 py-1 mx-2 mb-1"
                      onClick={openForm()}
                    >
                      <i className="fas fa-plus" />
                    </button>

                  )}
                </div>
                {form && (
                  <TagsFilter tags={tags} handleClose={closeForm} setTags={setTags} />
                )}
              </div>
            </form>
          </div>
          <div className="card-footer text-center">
            <button 
              type="button"
              className={(machineData.isActive) ? 'btn btn-secondary bg-danger btn-sm mr-3' : 'btn btn-secondary bg-success btn-sm mr-3'}
              onClick={changeMachineStatus}
              >
                {(machineData.isActive) ? "Deaktywuj" : "Aktywuj"}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm mr-3"
              onClick={discardChanges}
              disabled={!isUnsavedData}
            >
              {TRL_Pack.buttons.cancel}
            </button>
            <button
              type="button"
              disabled={!machineData.isActive}
              className="btn btn-primary btn-sm"
              onClick={saveMachine}
            >
              {TRL_Pack.buttons.save}
            </button>
          </div>
        </div>
      </div>
      <div className="col-12 col-md-6">
        <div className="card h-100">
          <h5 className="card-header position-relative">
            {isCoffee ? 'Stany kontenerów' : 'Informacje'}
            {isCoffee && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm position-absolute"
                style={{
                  right: '1.25rem',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
                onClick={() => setShowIngredientsModal(true)}
                title="Zarządzaj kontenerami"
              >
                <i className="fas fa-pen" />
              </button>
            )}
          </h5>
          <div className="card-body d-flex flex-column align-items-stretch justify-content-center">
            {isCoffee ? (
              <>
                <div className="row">
                  <div className="col-12 pe-none" style={{ fontWeight: 400 }}>
                    <CoffeeContainers
                      machineId={machineId}
                      refreshKey={ingredientsRefreshKey}
                    />
                  </div>
                </div>
                <MachineIngredientsModal
                  open={showIngredientsModal}
                  handleClose={() => setShowIngredientsModal(false)}
                  machineId={machineId}
                  machineData={initialMachineData}
                  machineFullName={
                    initialMachineData?.MachineFullName ||
                    initialMachineData?.FullName ||
                    initialMachineData?.MachineName ||
                    ''
                  }
                  onSaved={() => setIngredientsRefreshKey(prev => prev + 1)}
                />
              </>
            ) : (
              initialMachineData.LastVisit ? (
                <div className="row mb-3">
                  <div className="col-lg-4 mb-2 mb-lg-0 text-lg-right">Ostatnia wizyta</div>
                  <strong className="col-lg-8 text-center text-lg-left">
                    {initialMachineData.LastVisit}
                  </strong>
                </div>
              ) : null
            )}
          </div>
          <div className="card-footer text-center" aria-hidden="true">
            <button
              type="button"
              className="btn btn-secondary bg-danger btn-sm mr-3 invisible"
              tabIndex={-1}
            >
              Deaktywuj
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm mr-3 invisible"
              tabIndex={-1}
            >
              {TRL_Pack.buttons.cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm invisible"
              tabIndex={-1}
            >
              {TRL_Pack.buttons.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}